import { useForm, Controller } from "react-hook-form";
import { useState } from "react";
import { CheckCircle2, ChevronDown, Loader2, Send, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { submitInquiry } from "@/services/api";
import { SERVICE_OPTIONS } from "@/data/site";

const field =
  "w-full rounded-xl border border-[#e7e9ef] bg-white px-4 py-3.5 text-[#1B1B1B] placeholder-[#1B1B1B]/35 outline-none transition-colors focus:border-[#142A5E]";
const errCls = "mt-1.5 text-sm text-red-500";

export default function ContactForm() {
  const [sent, setSent] = useState(false);
  const {
    register, handleSubmit, control, reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { name: "", company: "", email: "", phone: "", service: [], message: "", agreed: false },
  });

  const onSubmit = async (data) => {
    try {
      await submitInquiry({ ...data, service: data.service.join(", ") });
      setSent(true);
      toast.success("Thank you! Our team will contact you shortly.");
      reset();
    } catch {
      toast.error("We couldn't send your inquiry. Please try again.");
    }
  };

  if (sent) {
    return (
      <motion.div
        data-testid="contact-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center rounded-3xl border border-[#e7e9ef] bg-white p-12 text-center"
      >
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#2FA86A]/12 text-[#2FA86A]">
          <CheckCircle2 className="h-9 w-9" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#1B1B1B]">Thank You!</h3>
        <p className="mt-2 text-[#1B1B1B]/60">Our team will contact you shortly.</p>
        <button
          data-testid="contact-send-another"
          onClick={() => setSent(false)}
          className="mt-6 rounded-full border border-[#142A5E]/20 px-5 py-2.5 font-semibold text-[#142A5E] transition-colors hover:border-[#142A5E]"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-3xl border border-[#e7e9ef] bg-white p-7 md:p-9"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Full Name *</label>
          <input
            data-testid="contact-name"
            className={field}
            {...register("name", { required: "Name is required" })}
          />
          {errors.name && <p className={errCls}>{errors.name.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Company Name</label>
          <input data-testid="contact-company" className={field} {...register("company")} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Email *</label>
          <input
            data-testid="contact-email"
            className={field}
            {...register("email", {
              required: "Email is required",
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
            })}
          />
          {errors.email && <p className={errCls}>{errors.email.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Phone</label>
          <input data-testid="contact-phone" className={field} {...register("phone")} />
        </div>
      </div>

      <div className="mt-5">
        <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Service Required</label>
        <Controller
          control={control}
          name="service"
          render={({ field: { value, onChange } }) => (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="contact-service"
                  className="flex min-h-[3.25rem] w-full flex-wrap items-center gap-1.5 rounded-xl border border-[#e7e9ef] bg-white px-4 py-2.5 text-left outline-none transition-colors focus:border-[#142A5E]"
                >
                  {value.length === 0 ? (
                    <span className="text-[#1B1B1B]/35">Select services</span>
                  ) : (
                    value.map((s) => (
                      <Badge
                        key={s}
                        variant="secondary"
                        className="gap-1 rounded-full border-transparent bg-[#142A5E]/8 px-2.5 py-1 font-medium text-[#142A5E] hover:bg-[#142A5E]/8"
                      >
                        {s}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(value.filter((v) => v !== s));
                          }}
                          className="rounded-full hover:text-[#0d1c40]"
                        >
                          <X className="h-3 w-3" />
                        </span>
                      </Badge>
                    ))
                  )}
                  <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-[#1B1B1B]/40" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="max-h-72 w-[--radix-popover-trigger-width] overflow-auto p-2"
              >
                {SERVICE_OPTIONS.map((s) => {
                  const checked = value.includes(s);
                  return (
                    <label
                      key={s}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-[#1B1B1B] hover:bg-[#FEF7F2]"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) =>
                          onChange(v ? [...value, s] : value.filter((x) => x !== s))
                        }
                        className="border-[#142A5E]/40 data-[state=checked]:bg-[#142A5E] data-[state=checked]:border-[#142A5E]"
                      />
                      {s}
                    </label>
                  );
                })}
              </PopoverContent>
            </Popover>
          )}
        />
      </div>

      <div className="mt-5">
        <label className="mb-1.5 block text-sm font-medium text-[#1B1B1B]/70">Message *</label>
        <textarea
          data-testid="contact-message"
          rows={4}
          className={`${field} resize-none`}
          placeholder="Tell us how we can help..."
          {...register("message", { required: "Please add a short message" })}
        />
        {errors.message && <p className={errCls}>{errors.message.message}</p>}
      </div>

      <div className="mt-5 flex items-start gap-3">
        <Controller
          control={control}
          name="agreed"
          rules={{ required: "Please accept the privacy policy" }}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              data-testid="contact-agree"
              checked={value}
              onCheckedChange={onChange}
              className="mt-0.5 border-[#142A5E]/40 data-[state=checked]:bg-[#142A5E] data-[state=checked]:border-[#142A5E]"
            />
          )}
        />
        <label className="text-sm text-[#1B1B1B]/65">
          I agree to the <span className="font-semibold text-[#142A5E]">Privacy Policy</span> and consent to being contacted.
        </label>
      </div>
      {errors.agreed && <p className={errCls}>{errors.agreed.message}</p>}

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={isSubmitting}
        className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#142A5E] px-7 py-4 font-semibold text-white transition-colors hover:bg-[#0d1c40] disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {isSubmitting ? "Sending..." : "Submit Inquiry"}
      </button>
    </form>
  );
}
